import React, { useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { updateScopeDod } from '../../utils/shapeUpApi';

const formatWhen = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString();
};

/**
 * A scope's definition of done: the organization's checklist, ticked here rather
 * than in Jira because it is the team's own standard and not part of any
 * workflow. Jira hears about it once, in a comment, when the list is complete.
 */
const DodChecklist = ({ scope, items = [], standard = '', readOnly = false, onScopeChanged }) => {
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);

  const checked = scope.dodChecked || {};
  const doneCount = items.filter(item => checked[item.id]).length;
  const met = items.length > 0 && doneCount === items.length;

  if (!items.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No definition of done yet. An owner or admin can set one in the organization's
        Shape Up settings, and it will appear on every scope.
      </Typography>
    );
  }

  const handleToggle = async (item) => {
    setBusyId(item.id);
    setError(null);
    setNote(null);
    try {
      const result = await updateScopeDod(scope.id, item.id, !checked[item.id]);
      if (onScopeChanged) onScopeChanged(result.scope);
      if (result.warning) setError(result.warning);
      else if (result.commented) setNote(`Definition of done met. ${scope.jiraKey} was commented in Jira.`);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not save that check.');
    } finally {
      setBusyId(null);
    }
  };

  // Preserve the order the checklist was written in rather than sorting groups.
  const groups = items.reduce((acc, item) => {
    const key = item.group || '';
    const existing = acc.find(entry => entry.name === key);
    if (existing) existing.items.push(item);
    else acc.push({ name: key, items: [item] });
    return acc;
  }, []);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Definition of done</Typography>
        <Chip
          size="small"
          variant={met ? 'filled' : 'outlined'}
          color={met ? 'success' : 'default'}
          icon={met ? <CheckCircleIcon /> : undefined}
          label={met ? `Met · ${formatWhen(scope.dodCompletedAt) || 'just now'}` : `${doneCount} of ${items.length}`}
        />
      </Box>

      {standard && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {standard}
        </Typography>
      )}

      {error && <Alert severity="warning" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}
      {note && <Alert severity="success" sx={{ mb: 1 }} onClose={() => setNote(null)}>{note}</Alert>}

      {groups.map(group => (
        <Box key={group.name || 'ungrouped'} sx={{ mb: 1 }}>
          {group.name && (
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, letterSpacing: '.06em', color: 'text.secondary' }}
            >
              {group.name.toUpperCase()}
            </Typography>
          )}

          {group.items.map(item => {
            const tick = checked[item.id];
            const who = tick && tick.by ? `Checked by ${tick.by}${formatWhen(tick.at) ? ` on ${formatWhen(tick.at)}` : ''}` : '';
            return (
              <Box key={item.id} sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Tooltip title={item.detail || who || ''} placement="top-start">
                  <FormControlLabel
                    sx={{ alignItems: 'flex-start', mr: 0, ml: 0 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={Boolean(tick)}
                        disabled={readOnly || busyId === item.id}
                        onChange={() => handleToggle(item)}
                        sx={{ py: 0.25 }}
                      />
                    }
                    label={
                      <Box sx={{ py: 0.25 }}>
                        <Typography
                          variant="body2"
                          sx={{ color: tick ? 'text.secondary' : 'text.primary' }}
                        >
                          {item.label}
                        </Typography>
                        {who && (
                          <Typography variant="caption" color="text.disabled">{who}</Typography>
                        )}
                      </Box>
                    }
                  />
                </Tooltip>
                {busyId === item.id && <CircularProgress size={12} sx={{ mt: 1.2 }} />}
              </Box>
            );
          })}
        </Box>
      ))}
    </Box>
  );
};

export default DodChecklist;
